import type { OnlineEventViewDto } from '@/api/webs'
import type { WebinarRoomRecordingViewDto, WebinarRoomWaitingImageViewDto } from '@/api/webinarRoom'
import { markRow } from '@/components/knowledgeBase/brandGuidelines/brandLogos'
import type { KnowledgeBaseFileViewDto } from '@/types/knowledgeBase'
import { formatBytes } from '@/utils/fileSize'
import { eventZoneInstant } from '@/utils/timeZones'
import { ROOM_ENTRY_OPENS_MINUTES_BEFORE_START } from '@/utils/webinarRoomSetup'
import { uploadedByLine } from './roomRecording'

/** Viewers are let in, and the waiting video starts, this long before the start. */
export const ENTRY_OPENS_MS = ROOM_ENTRY_OPENS_MINUTES_BEFORE_START * 60_000
const ENTRY = `${ROOM_ENTRY_OPENS_MINUTES_BEFORE_START} minutes before the start`

/** The server's sign-off item for a waiting video that is set but not ready, shown on the card's pill as it comes. */
export const WAITING_VIDEO_FIELD_NAME = 'OnlineEvent.WaitingVideo'

/** The recording's file rules, with no length limit: it loops or plays once, whatever its length. */
export const WAITING_VIDEO_LIMITS = 'MP4 or MOV, up to 5 GB. A short clip works best.'

/**
 * The image shown instead of a video, from the computer only. `WebinarRoomLimits.WaitingImageContentTypes` and
 * `MaxWaitingImageBytes` on the server; the server checks again, against the file's first bytes too.
 */
export const WAITING_IMAGE_LIMITS = 'PNG, JPG or WebP, up to 10 MB. 16:9 fits best.'
export const WAITING_IMAGE_ACCEPT = '.png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp'
export const WAITING_IMAGE_MAX_BYTES = 10 * 1024 * 1024
const WAITING_IMAGE_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp'])

/** Why a chosen image can't be used, in the server's words, or null when it can. The browser types a file by its extension. */
export function waitingImageFileProblem(file: { type: string; size: number }): string | null {
    if (!WAITING_IMAGE_TYPES.has(file.type)) return 'Choose a PNG, JPG or WebP image.'
    if (file.size === 0) return 'The image’s file is empty.'
    if (file.size > WAITING_IMAGE_MAX_BYTES) return 'The image can be at most 10 MB.'
    return null
}

/** "1920 × 1080 · 412 KB · Uploaded by Kevin Mistry, today, 5:09 PM", the size read from the image once it has loaded. */
export function waitingImageFacts(image: WebinarRoomWaitingImageViewDto, size: { width: number; height: number } | null, now: Date): string {
    return [
        size ? `${size.width} × ${size.height}` : null,
        formatBytes(image.SizeBytes),
        uploadedByLine(image, now),
    ].filter(Boolean).join(' · ')
}

export type WaitingView = 'empty' | 'uploading' | 'preparing' | 'failed' | 'ready'

/** The waiting video's state. An upload running in this browser shows as uploading before the reread web says so. */
export function waitingView(video: WebinarRoomRecordingViewDto | null | undefined, uploadRunning: boolean): WaitingView {
    if (uploadRunning) return 'uploading'
    switch (video?.State) {
        case undefined: return 'empty'
        case 'Uploading': return 'uploading'
        case 'Preparing': return 'preparing'
        case 'Failed': return 'failed'
        case 'Ready': return 'ready'
    }
}

/**
 * The logo the preview's slate shows: the one for light backgrounds, as the page bakes it for the light theme viewers
 * open on, or the file marked for neither that stands in for it. Null leaves the slate showing the company's name.
 */
export function logoForLightBackgrounds(files: readonly KnowledgeBaseFileViewDto[]): string | null {
    const row = markRow(files, 'Logo')
    const file = row.kind === 'either' ? row.file : row.cells.Light?.file ?? null
    return file?.ContentType.startsWith('image/') && file.DownloadUrl ? file.DownloadUrl : null
}

/** The session's start as an instant, from the view's wall clock in the event's zone; null before it is set. */
export function sessionStartMs(event: Pick<OnlineEventViewDto, 'StartDateTime' | 'TimeZone'>): number | null {
    return instantOf(event.StartDateTime, event.TimeZone)
}

export function sessionEndMs(event: Pick<OnlineEventViewDto, 'EndDateTime' | 'TimeZone'>): number | null {
    return instantOf(event.EndDateTime, event.TimeZone)
}

const instantOf = (wallClock: string | null | undefined, timeZone: string | null | undefined): number | null =>
    wallClock ? eventZoneInstant(wallClock, timeZone)?.getTime() ?? null : null

/**
 * The preview's clock: a viewer let in as the room opens, so the countdown runs from the opening however far off the
 * session is, and starts over each time it reaches the start. Without a start the slate shows no countdown, so the real time does.
 */
export function previewNow(startMs: number | null, openedMs: number, nowMs: number): number {
    if (startMs === null) return nowMs
    return startMs - ENTRY_OPENS_MS + ((nowMs - openedMs) % ENTRY_OPENS_MS)
}

/** The card's line under its title: the plain slate, how the video plays, or the image. */
export function waitingSubLine(shows: 'slate' | 'video' | 'image', playsOnce: boolean): string {
    if (shows === 'slate') return `Viewers are let in ${ENTRY} and wait on this slate with a countdown.`
    if (shows === 'image') return `Shown behind the countdown from when viewers are let in, ${ENTRY}.`
    return playsOnce
        ? `Each viewer sees it once from when they’re let in, ${ENTRY}, then the slate and countdown.`
        : `Plays on a loop from when viewers are let in, ${ENTRY}. It only starts again if it can finish before the session starts.`
}
